<template>
  <div>
    <a-skeleton active v-if="loading" />
    <div v-else>
      <a-collapse v-model="activeKey" class="mb-5">
        <a-collapse-panel
          v-for="(value, key) in data"
          :key="key"
          :header="getPaymentHeader(key, value)"
          :disabled="false"
        >
          <!-- Payment-specific bulk actions -->
          <div v-if="!loading" class="payment-bulk-actions mb-3">
            <a-row type="flex" justify="space-between" align="middle">
              <a-col>
                <a-space>
                  <a-checkbox
                    :indeterminate="getPaymentIndeterminate(value.payment)"
                    :checked="getPaymentCheckAll(value.payment)"
                    @change="(e) => onPaymentCheckAllChange(e, value.payment)"
                  >
                    {{ $t("action.selectAll") }}
                  </a-checkbox>
                  <a-tag
                    v-if="getPaymentSelectedCount(value.payment) > 0"
                    color="blue"
                  >
                    {{ getPaymentSelectedCount(value.payment) }}
                    {{ $t("action.selected") }}
                  </a-tag>
                </a-space>
              </a-col>
              <a-col v-if="getPaymentSelectedCount(value.payment) > 0">
                <a-space>
                  <a-button
                    v-if="
                      bulkEditMode && currentEditingPayment === value.payment
                    "
                    type="primary"
                    @click="savePaymentBulkReductions(value.payment)"
                    :loading="loadingSave"
                    :disabled="loadingSave"
                  >
                    <a-icon type="save" />
                    {{ $t("action.saveAll") }}
                  </a-button>
                  <a-button
                    v-if="
                      bulkEditMode && currentEditingPayment === value.payment
                    "
                    @click="cancelPaymentBulkEdit"
                  >
                    <a-icon type="close" />
                    {{ $t("all.cancel") }}
                  </a-button>
                </a-space>
              </a-col>
            </a-row>

            <!-- Single reduction value input for selected rows -->
            <div
              v-if="
                bulkEditMode &&
                currentEditingPayment === value.payment &&
                getPaymentSelectedCount(value.payment) > 0
              "
              class="bulk-value-input mt-3"
            >
              <a-row type="flex" align="middle">
                <a-col :span="6">
                  <label class="bulk-input-label">{{
                    $t("action.applySameReduction")
                  }}</label>
                </a-col>
                <a-col :span="8">
                  <a-input-number
                    :value="bulkReductionValue"
                    :min="0"
                    :max="
                      reductionType === 'pourcentage'
                        ? 100
                        : getMaxReductionValue(value.payment)
                    "
                    :placeholder="
                      reductionType === 'amount'
                        ? getBuildingCurrency() === 'TND'
                          ? $t('reduction.amountInTND')
                          : $t('reduction.amountInKES')
                        : $t('reduction.percentage')
                    "
                    @change="(val) => (bulkReductionValue = val)"
                    style="width: 100%"
                  />
                </a-col>
                <a-col :span="4" class="text-center">
                  <a-tag color="green">
                    {{ getReductionTypeLabel() }}
                  </a-tag>
                </a-col>
              </a-row>
            </div>
          </div>

          <a-table
            :columns="columns"
            :data-source="value.reductions"
            :pagination="false"
            :row-selection="
              bulkEditMode && currentEditingPayment === value.payment
                ? rowSelection
                : null
            "
            :row-key="(record) => `${record.feeName}#${value.payment}`"
            :row-class-name="
              (record) => (record.fullyPaid ? 'fully-paid-row' : '')
            "
          >
            <span slot="feeName" slot-scope="text, record">
              <p>
                {{ formatFeeName(text, value.daily)}}
              </p>
            </span>
            <span slot="repReductionValue" slot-scope="text, record">
              <span
                v-if="
                  (editingReduction &&
                    editingReduction.feeName == record.feeName &&
                    editingReduction.payment == value.payment) ||
                  (bulkEditMode &&
                    currentEditingPayment === value.payment &&
                    isPaymentRowSelected(record, value.payment))
                "
              >
                <a-input-number
                  v-if="
                    editingReduction &&
                    editingReduction.feeName == record.feeName &&
                    editingReduction.payment == value.payment
                  "
                  :value="editingReduction.value"
                  :min="0"
                  :max="
                    reductionType == 'pourcentage'
                      ? 100
                      : Number(record.feeBeforeRed)
                  "
                  @change="(val) => (editingReduction.value = val)"
                />
                <span
                  v-else-if="
                    bulkEditMode &&
                    currentEditingPayment === value.payment &&
                    isPaymentRowSelected(record, value.payment)
                  "
                >
                  <a-tag color="blue">
                    {{
                      bulkReductionValue !== null
                        ? parseFloat(bulkReductionValue).toFixed(2)
                        : "0.00"
                    }}
                    {{ getReductionTypeLabel() }}
                  </a-tag>
                </span>
              </span>
              <a-tag color="green" v-else>
                <span v-if="reductionType == 'amount'">
                  {{
                    (record.feeBeforeRed - record.feeAfterReduction).toFixed(2)
                  }}
                  {{ getReductionTypeLabel() }}
                </span>
                <span v-else> {{ Number(text).toFixed(2) }} % </span>
              </a-tag>
            </span>
            <span slot="feeAfterReduction" slot-scope="text, record">
              <span
                v-if="
                  (editingReduction &&
                    editingReduction.feeName == record.feeName &&
                    editingReduction.payment == value.payment) ||
                  (bulkEditMode &&
                    currentEditingPayment === value.payment &&
                    isPaymentRowSelected(record, value.payment))
                "
              >
                <span
                  v-if="
                    editingReduction &&
                    editingReduction.feeName == record.feeName &&
                    editingReduction.payment == value.payment
                  "
                >
                  <span v-if="reductionType == 'amount'">
                    {{
                      (record.feeBeforeRed - editingReduction.value).toFixed(2)
                    }}
                    {{ getReductionTypeLabel() }}
                  </span>
                  <span v-else>
                    {{
                      (
                        record.feeBeforeRed -
                        (record.feeBeforeRed * editingReduction.value) / 100
                      ).toFixed(2)
                    }}
                  </span>
                </span>
                <span
                  v-else-if="
                    bulkEditMode &&
                    currentEditingPayment === value.payment &&
                    isPaymentRowSelected(record, value.payment)
                  "
                >
                  <span v-if="reductionType == 'amount'">
                    {{
                      (record.feeBeforeRed - (bulkReductionValue || 0)).toFixed(
                        2
                      )
                    }}
                    {{ getReductionTypeLabel() }}
                  </span>
                  <span v-else>
                    {{
                      (
                        record.feeBeforeRed -
                        (record.feeBeforeRed * (bulkReductionValue || 0)) / 100
                      ).toFixed(2)
                    }}
                  </span>
                </span>
              </span>
              <span v-else>
                {{ Number(text).toFixed(2) }}
              </span>
            </span>
            <div slot="action" slot-scope="text, record">
              <span v-if="record.fullyPaid">
                <a-tag color="green">
                  <a-icon type="check-circle" />
                  {{ $t("action.fullyPaid") }}
                </a-tag>
              </span>
              <span v-else-if="!bulkEditMode">
                <a-button
                  v-if="
                    !(
                      editingReduction &&
                      editingReduction.feeName == record.feeName &&
                      editingReduction.payment == value.payment
                    )
                  "
                  type="primary"
                  :disabled="
                    editingReduction &&
                    (editingReduction.feeName != record.feeName ||
                      editingReduction.payment != value.payment)
                  "
                  @click="
                    () => {
                      editingReduction = {
                        feeName: record.feeName,
                        payment: value.payment,
                        value:
                          reductionType == 'amount'
                            ? record.feeBeforeRed - record.feeAfterReduction
                            : record.repReductionValue,
                        record: record,
                      };
                    }
                  "
                  class="mr-3"
                >
                  {{ $t("action.modifier") }}
                </a-button>
                <div
                  v-if="
                    editingReduction &&
                    editingReduction.feeName == record.feeName &&
                    editingReduction.payment == value.payment
                  "
                >
                  <a-button
                    @click="saveReduction"
                    :loading="loadingSave"
                    :disabled="loadingSave"
                    type="primary"
                    class="mr-3"
                  >
                    {{ $t("action.enregistrer") }}
                  </a-button>
                  <a-button
                    @click="
                      () => {
                        editingReduction = null;
                      }
                    "
                    class="mr-3"
                  >
                    {{ $t("action.annuler") }}
                  </a-button>
                </div>
              </span>
              <span
                v-else-if="
                  bulkEditMode &&
                  currentEditingPayment === value.payment &&
                  isPaymentRowSelected(record, value.payment) &&
                  !record.fullyPaid
                "
              >
                <a-tag color="blue">
                  <a-icon type="edit" />
                  {{ $t("action.selected") }}
                </a-tag>
              </span>
            </div>
          </a-table>
        </a-collapse-panel>
      </a-collapse>
    </div>
  </div>
</template>

<script>
/* eslint-disable */

import { mapState } from "vuex";
import apiClient from "@/services/axios";
import Vue from "vue";
import VueRouter from "vue-router";
import moment from "moment";
import { formatFeeName as formatFeeNameUtil } from "@/utilities/settings";
import { Space } from "ant-design-vue";
import { getReductionTypeLabel, getBuildingCurrency } from "@/utilities/countrySpecific";

const frMonths = {};
const months = moment.months();
for (let i = 0; i < months.length; i++) frMonths[i + 1] = months[i];
Vue.use(VueRouter);

export default {
  props: {
    studentId: {
      required: true,
    },
  },
  components: { "a-space": Space },
  async created() {
    this.$gtag.pageview({
      page_path: this.$route.path,
      page_location: window.location.href,
      page_title: document.title + " - Réduction de base",
    });
  },
  mounted() {
    const building = this.user.building.find(
      (el) => el.dbName == this.settings.activeBuilding
    );
    if (building) this.reductionType = building.reductionType || "pourcentage";

    this.getRepRuductions();
  },

  data() {
    return {
      editingReduction: null,
      activeKey: [],
      frMonths,
      data: [],
      loading: true,
      loadingSave: false,
      moment,
      reductionType: "amount",
      // Bulk editing state
      bulkEditMode: false,
      selectedRows: [],
      bulkEditValues: {},
      indeterminate: false,
      checkAll: false,
      // Per-payment bulk editing
      currentEditingPayment: null,
      bulkReductionValue: null,
      paymentSelectedRows: {}, // { paymentId: [rowKeys] }
    };
  },
  computed: {
    ...mapState(["settings", "user"]),
    columns() {
      return [
        {
          title: this.$t("reduction.designation"),
          dataIndex: "feeName",
          key: "feeName",
          scopedSlots: {
            customRender: "feeName",
          },
        },
        {
          title:
            this.reductionType === "amount"
              ? getBuildingCurrency() === "TND"
                ? this.$t("reduction.reductionTND")
                : this.$t("reduction.reductionKES")
              : this.$t("reduction.reductionPercent"),
          dataIndex: "repReductionValue",
          key: "repReductionValue",
          scopedSlots: {
            customRender: "repReductionValue",
          },
        },
        {
          title: this.$t("reduction.amountBeforeReduction"),
          dataIndex: "feeBeforeRed",
          key: "feeBeforeRed",
        },
        {
          title: this.$t("reduction.amountAfterReduction"),
          key: "feeAfterReduction",
          dataIndex: "feeAfterReduction",
          scopedSlots: {
            customRender: "feeAfterReduction",
          },
        },
        {
          title: this.$t("reduction.actions"),
          key: "action",
          scopedSlots: {
            customRender: "action",
          },
        },
      ];
    },
    rowSelection() {
      return {
        selectedRowKeys: this.getCurrentPaymentSelectedRows(),
        onChange: (selectedRowKeys, selectedRows) => {
          this.onPaymentSelectChange(
            selectedRowKeys,
            this.currentEditingPayment
          );
        },
        getCheckboxProps: (record) => ({
          props: {
            disabled: record.fullyPaid,
            name: record.feeName,
          },
        }),
      };
    },
  },
  methods: {
    formatFeeName(key, daily = false) {
      return formatFeeNameUtil(key, daily, this.$t.bind(this));
    },
    getReductionTypeLabel() {
      return getReductionTypeLabel(this.reductionType);
    },
    getBuildingCurrency() {
      return getBuildingCurrency();
    },
    getRepRuductions(activeKey) {
      apiClient
        .get("/payment/reductionRep/" + this.studentId)
        .then((res) => {
          this.data = res.data;
          console.log("Loaded reduction data:", res.data);
          const keys = Object.keys(res.data);

          if (keys.length > 0) {
            this.activeKey = activeKey || [keys[0]];
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    saveReduction() {
      this.loadingSave = true;

      // convert amount to % by using editingReduction.record.feeBeforeRed
      if (this.reductionType == "amount")
        this.editingReduction.value =
          (this.editingReduction.value /
            this.editingReduction.record.feeBeforeRed) *
          100;

      const data = {
        value: this.editingReduction.value,
        feeName: this.editingReduction.feeName,
        payment: this.editingReduction.payment,
      };

      apiClient
        .patch("/payment/reductionRep/", data)
        .then((res) => {
          this.getRepRuductions(this.activeKey);
          this.$emit("updateActivities");
          this.$emit("updateClassrooms");
        })
        .finally(() => {
          this.loadingSave = false;
          this.editingReduction = null;
        });
    },
    // Payment header with selection info
    getPaymentHeader(key, value) {
      const selectedCount = this.getPaymentSelectedCount(value.payment);
      if (selectedCount > 0) {
        const pluralSuffix =
          selectedCount > 1
            ? this.$i18n.locale === "fr-FR"
              ? "s"
              : ""
            : "";
        return `${key} (${selectedCount} ${this.$t(
          "reduction.selected"
        )}${pluralSuffix})`;
      }
      return key;
    },
    // Per-payment bulk editing methods
    getPaymentSelectedCount(paymentId) {
      return this.paymentSelectedRows[paymentId]
        ? this.paymentSelectedRows[paymentId].length
        : 0;
    },
    getPaymentCheckAll(paymentId) {
      const selectedCount = this.getPaymentSelectedCount(paymentId);
      const totalCount = this.getPaymentTotalCount(paymentId);
      return selectedCount > 0 && selectedCount === totalCount;
    },
    getPaymentIndeterminate(paymentId) {
      const selectedCount = this.getPaymentSelectedCount(paymentId);
      const totalCount = this.getPaymentTotalCount(paymentId);
      return selectedCount > 0 && selectedCount < totalCount;
    },
    getPaymentTotalCount(paymentId) {
      const paymentData = Object.values(this.data).find(
        (p) => p.payment === paymentId
      );
      if (!paymentData) return 0;
      return paymentData.reductions.filter((record) => !record.fullyPaid)
        .length;
    },
    getCurrentPaymentSelectedRows() {
      return this.paymentSelectedRows[this.currentEditingPayment] || [];
    },
    onPaymentSelectChange(selectedRowKeys, paymentId) {
      console.log("Payment selection changed:", paymentId, selectedRowKeys);
      this.$set(this.paymentSelectedRows, paymentId, selectedRowKeys);

      // Automatically enter bulk edit mode when rows are selected
      if (selectedRowKeys.length > 0) {
        this.enterPaymentBulkEditMode(paymentId);
      } else {
        // Only exit bulk edit mode if no rows selected in ANY payment
        const hasAnySelections = Object.values(this.paymentSelectedRows).some(
          (rows) => rows.length > 0
        );
        if (!hasAnySelections) {
          this.cancelPaymentBulkEdit();
        }
      }
    },
    onPaymentCheckAllChange(e, paymentId) {
      const paymentData = Object.values(this.data).find(
        (p) => p.payment === paymentId
      );
      if (!paymentData) return;

      const allSelectableKeys = paymentData.reductions
        .filter((record) => !record.fullyPaid)
        .map((record) => `${record.feeName}#${paymentId}`);

      this.$set(
        this.paymentSelectedRows,
        paymentId,
        e.target.checked ? allSelectableKeys : []
      );

      // Automatically enter bulk edit mode when rows are selected
      if (e.target.checked && allSelectableKeys.length > 0) {
        this.enterPaymentBulkEditMode(paymentId);
      } else if (!e.target.checked) {
        // Only exit bulk edit mode if no rows selected in ANY payment
        const hasAnySelections = Object.values(this.paymentSelectedRows).some(
          (rows) => rows.length > 0
        );
        if (!hasAnySelections) {
          this.cancelPaymentBulkEdit();
        }
      }
    },
    enterPaymentBulkEditMode(paymentId) {
      this.bulkEditMode = true;
      this.currentEditingPayment = paymentId;
      this.editingReduction = null; // Clear single edit mode

      // Only initialize bulk reduction value if not already set
      if (
        this.bulkReductionValue === null ||
        this.bulkReductionValue === undefined
      ) {
        const selectedRows = this.paymentSelectedRows[paymentId] || [];
        console.log("Entering bulk edit mode for payment:", paymentId);
        console.log("Selected rows:", selectedRows);

        if (selectedRows.length > 0) {
          const firstRowKey = selectedRows[0];
          const [feeName] = firstRowKey.split("#");
          const paymentData = Object.values(this.data).find(
            (p) => p.payment === paymentId
          );
          if (paymentData) {
            const record = paymentData.reductions.find(
              (r) => r.feeName === feeName
            );
            if (record) {
              this.bulkReductionValue =
                this.reductionType === "amount"
                  ? record.feeBeforeRed - record.feeAfterReduction
                  : record.repReductionValue;
              console.log(
                "Initialized bulk reduction value:",
                this.bulkReductionValue
              );
            }
          }
        } else {
          // If no rows selected, initialize with 0
          this.bulkReductionValue = 0;
          console.log("No rows selected, initialized with 0");
        }
      } else {
        console.log(
          "Keeping existing bulk reduction value:",
          this.bulkReductionValue
        );
      }
    },
    cancelPaymentBulkEdit() {
      this.bulkEditMode = false;
      this.currentEditingPayment = null;
      this.bulkReductionValue = null;

      // Clear all selections when canceling
      Object.keys(this.paymentSelectedRows).forEach((paymentId) => {
        this.$set(this.paymentSelectedRows, paymentId, []);
      });
    },
    getMaxReductionValue(paymentId) {
      const paymentData = Object.values(this.data).find(
        (p) => p.payment === paymentId
      );
      if (!paymentData) return 0;

      const selectedRows = this.paymentSelectedRows[paymentId] || [];
      let maxValue = 0;

      selectedRows.forEach((rowKey) => {
        const [feeName] = rowKey.split("#");
        const record = paymentData.reductions.find(
          (r) => r.feeName === feeName
        );
        if (record) {
          maxValue = Math.max(maxValue, record.feeBeforeRed);
        }
      });

      return maxValue;
    },
    async savePaymentBulkReductions(paymentId) {
      const selectedRows = this.paymentSelectedRows[paymentId] || [];
      if (selectedRows.length === 0) {
        this.$message.warning(this.$t("message.noSelection"));
        return;
      }

      if (
        this.bulkReductionValue === null ||
        this.bulkReductionValue === undefined ||
        this.bulkReductionValue === ""
      ) {
        this.$message.warning(this.$t("message.enterReductionValue"));
        return;
      }

      this.loadingSave = true;

      try {
        const updates = [];
        const paymentData = Object.values(this.data).find(
          (p) => p.payment === paymentId
        );

        if (!paymentData) {
          this.$message.error(this.$t("message.paymentDataNotFound"));
          return;
        }

        selectedRows.forEach((rowKey) => {
          const [feeName] = rowKey.split("#");
          const record = paymentData.reductions.find(
            (r) => r.feeName === feeName
          );

          if (record) {
            // Convert amount to percentage if needed
            let finalValue = this.bulkReductionValue;
            if (this.reductionType === "amount") {
              finalValue =
                (this.bulkReductionValue / record.feeBeforeRed) * 100;
            }

            updates.push({
              payment: paymentId,
              feeName,
              value: finalValue,
            });
          }
        });

        console.log("Selected rows:", selectedRows);
        console.log("Bulk reduction value:", this.bulkReductionValue);
        console.log("Updates array:", updates);

        if (updates.length === 0) {
          this.$message.warning(this.$t("message.noValidUpdates"));
          return;
        }

        const response = await apiClient.patch("/payment/reductionRep/bulk", {
          updates,
        });

        this.$message.success(
          this.$t("message.bulkUpdateSuccess", { count: updates.length })
        );

        // Refresh data and exit bulk edit mode
        await this.getRepRuductions(this.activeKey);
        this.cancelPaymentBulkEdit();
        this.$emit("updateActivities");
        this.$emit("updateClassrooms");
      } catch (error) {
        console.error("Bulk update error:", error);
        this.$message.error(this.$t("message.bulkUpdateError"));
      } finally {
        this.loadingSave = false;
      }
    },
    isPaymentRowSelected(record, payment) {
      const rowKey = `${record.feeName}#${payment}`;
      return (
        this.paymentSelectedRows[payment] &&
        this.paymentSelectedRows[payment].includes(rowKey)
      );
    },
  },
};
</script>

<style scoped>
.bulk-actions-toolbar {
  background: #f8f9fa;
  padding: 16px;
  border-radius: 6px;
  border: 1px solid #e8e8e8;
  margin-bottom: 16px;
}

.payment-bulk-actions {
  background: #f0f8ff;
  padding: 12px;
  border-radius: 4px;
  border: 1px solid #d6e4ff;
  margin-bottom: 12px;
}

.bulk-value-input {
  background: #fff7e6;
  padding: 12px;
  border-radius: 4px;
  border: 1px solid #ffd591;
}

.bulk-input-label {
  font-weight: 500;
  color: #1890ff;
  margin: 0;
}

.bulk-actions-toolbar .ant-row {
  width: 100%;
}

.ant-table-tbody > tr.selected-row {
  background-color: #e6f7ff;
}

.ant-table-tbody > tr.editing-row {
  background-color: #fff7e6;
}

.ant-table-tbody > tr.fully-paid-row {
  background-color: #f5f5f5;
  opacity: 0.6;
}

.ant-table-tbody > tr.fully-paid-row td {
  color: #999;
}

.ant-input-number {
  width: 100%;
}

.ant-tag {
  margin: 0;
}

.ant-space {
  width: 100%;
}

.ant-space-item {
  display: flex;
  align-items: center;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .bulk-actions-toolbar .ant-row {
    flex-direction: column;
    gap: 12px;
  }

  .bulk-actions-toolbar .ant-col {
    width: 100%;
  }

  .bulk-actions-toolbar .ant-space {
    justify-content: center;
  }
}
</style>
